import {
  ContentState,
  DataTable,
  FormField,
  Select,
  StatusPill,
  type DataTableColumn,
} from "@whitehaven/ui";
import type {
  AdminLeadListCursor,
  AdminLeadListItem,
} from "@whitehaven/contracts";
import {
  adminLeadListSourceOptions,
  adminLeadListStageOptions,
  ALL_LEAD_ASSIGNMENTS,
  ALL_LEAD_PIPELINE_STAGES,
  ALL_LEAD_SOURCE_KINDS,
  ASSIGNED_LEADS,
  defaultAdminLeadListFilters,
  hasActiveAdminLeadListFilters,
  leadPipelineStageLabels,
  leadSourceKindLabels,
  selectAdminLeadListItems,
  UNRECORDED_LEAD_SOURCE,
  UNASSIGNED_LEADS,
  type AdminLeadListFilters,
} from "./admin-lead-list-model";
import { createAdminLeadDetailHref } from "./lead-detail-route-model";
import { leadAssignableRoleLabels } from "./lead-assignment-model";

export type AdminLeadListNotice = "REFRESH_FAILED" | "LOAD_MORE_FAILED" | null;

interface AdminLeadListViewProps {
  readonly items: readonly AdminLeadListItem[];
  readonly nextCursor: AdminLeadListCursor | null;
  readonly filters: AdminLeadListFilters;
  readonly refreshing: boolean;
  readonly loadingMore: boolean;
  readonly notice: AdminLeadListNotice;
  readonly onFiltersChange: (filters: AdminLeadListFilters) => void;
  readonly onRefresh: () => void;
  readonly onLoadMore: () => void;
}

const leadDateFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Pacific/Honolulu",
});

export function formatAdminLeadDate(value: string): string | null {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? leadDateFormatter.format(date)
    : null;
}

function LeadDate({ value }: { readonly value: string }) {
  const formatted = formatAdminLeadDate(value);
  return formatted ? <time dateTime={value}>{formatted}</time> : "Unavailable";
}

function LeadIdentity({ item }: { readonly item: AdminLeadListItem }) {
  const detailHref = createAdminLeadDetailHref(item.leadId);

  return (
    <div className="crm-lead-list__identity">
      {detailHref ? (
        <a className="crm-lead-list__name" href={detailHref}>
          {item.displayName}
        </a>
      ) : (
        <span className="crm-lead-list__name">{item.displayName}</span>
      )}
      {item.emailNormalized && (
        <span className="crm-lead-list__channel">{item.emailNormalized}</span>
      )}
      {item.phoneE164 && (
        <span className="crm-lead-list__channel">{item.phoneE164}</span>
      )}
      {!item.emailNormalized && !item.phoneE164 && (
        <span className="crm-lead-list__channel">
          No contact channel recorded
        </span>
      )}
    </div>
  );
}

function LeadSource({ item }: { readonly item: AdminLeadListItem }) {
  if (!item.sourceKind) {
    return <span className="crm-lead-list__muted">Not captured</span>;
  }

  return (
    <div className="crm-lead-list__source">
      <span>{leadSourceKindLabels[item.sourceKind]}</span>
      {item.sourceKey && <small>Source: {item.sourceKey}</small>}
      {item.campaignKey && <small>Campaign: {item.campaignKey}</small>}
    </div>
  );
}

function RecordStatus({ item }: { readonly item: AdminLeadListItem }) {
  return (
    <div className="crm-lead-list__record-status">
      <StatusPill
        tone={item.leadRecordStatus === "ARCHIVED" ? "warning" : "neutral"}
      >
        Lead {item.leadRecordStatus === "ARCHIVED" ? "archived" : "active"}
      </StatusPill>
      {item.contactRecordStatus !== item.leadRecordStatus && (
        <StatusPill
          tone={item.contactRecordStatus === "ARCHIVED" ? "warning" : "neutral"}
        >
          Contact{" "}
          {item.contactRecordStatus === "ARCHIVED" ? "archived" : "active"}
        </StatusPill>
      )}
    </div>
  );
}

function LeadAssignee({ item }: { readonly item: AdminLeadListItem }) {
  if (item.assignedUserId === null) {
    return <span className="crm-lead-list__muted">Unassigned</span>;
  }

  if (!item.assignedTo) {
    return (
      <div className="crm-lead-list__source">
        <span>Unavailable assignee</span>
        <small>Recorded assignment retained</small>
      </div>
    );
  }

  return (
    <div className="crm-lead-list__source">
      <span>{item.assignedTo.displayLabel}</span>
      <small>{leadAssignableRoleLabels[item.assignedTo.role]}</small>
    </div>
  );
}

function PossibleMatches({ item }: { readonly item: AdminLeadListItem }) {
  const { duplicateSummary } = item;

  if (duplicateSummary.eligibility === "TARGET_INACTIVE") {
    return (
      <span className="crm-lead-list__muted">
        Not checked for an inactive record
      </span>
    );
  }

  if (duplicateSummary.candidateContactCount === 0) {
    return (
      <span className="crm-lead-list__muted">
        No exact normalized-channel matches
      </span>
    );
  }

  const matchLabel = duplicateSummary.matchKinds
    .map((kind) => (kind === "EMAIL" ? "Email" : "Phone"))
    .join(" + ");

  return (
    <div className="crm-lead-list__possible-matches">
      <StatusPill tone="warning">
        {duplicateSummary.candidateContactCount} possible
      </StatusPill>
      <small>{matchLabel}</small>
    </div>
  );
}

function filterLabel(count: number, loadedCount: number) {
  if (count === loadedCount) {
    return `${count} loaded ${count === 1 ? "lead" : "leads"} shown.`;
  }

  return `${count} of ${loadedCount} loaded leads shown.`;
}

export function AdminLeadListView({
  items,
  nextCursor,
  filters,
  refreshing,
  loadingMore,
  notice,
  onFiltersChange,
  onRefresh,
  onLoadMore,
}: AdminLeadListViewProps) {
  const visibleItems = selectAdminLeadListItems(items, filters);
  const filtersActive = hasActiveAdminLeadListFilters(filters);
  const busy = refreshing || loadingMore;
  const columns: readonly DataTableColumn<AdminLeadListItem>[] = [
    {
      id: "lead",
      header: "Lead",
      render: (item) => <LeadIdentity item={item} />,
      rowHeader: true,
      ...(filters.sort === "NAME_ASC"
        ? { sortDirection: "ascending" as const }
        : {}),
    },
    {
      id: "stage",
      header: "Stage",
      render: (item) => (
        <StatusPill>{leadPipelineStageLabels[item.pipelineStage]}</StatusPill>
      ),
    },
    {
      id: "source",
      header: "Source",
      render: (item) => <LeadSource item={item} />,
    },
    {
      id: "assignment",
      header: "Assigned to",
      render: (item) => <LeadAssignee item={item} />,
    },
    {
      id: "possible-matches",
      header: "Possible matches",
      render: (item) => <PossibleMatches item={item} />,
    },
    {
      id: "status",
      header: "Record status",
      render: (item) => <RecordStatus item={item} />,
    },
    {
      id: "created",
      header: "Created",
      render: (item) => <LeadDate value={item.createdAt} />,
      ...(filters.sort === "CREATED_DESC"
        ? { sortDirection: "descending" as const }
        : {}),
    },
    {
      id: "updated",
      header: "Updated",
      render: (item) => <LeadDate value={item.updatedAt} />,
      ...(filters.sort === "UPDATED_DESC"
        ? { sortDirection: "descending" as const }
        : {}),
    },
  ];

  if (items.length === 0) {
    return (
      <ContentState
        kind="empty"
        title="No active leads are available."
        actions={
          <button type="button" onClick={onRefresh}>
            Refresh lead list
          </button>
        }
      >
        <p>
          The authorized lead request completed without returning any active
          records.
        </p>
      </ContentState>
    );
  }

  return (
    <div className="crm-lead-list" aria-busy={busy ? "true" : undefined}>
      {notice && (
        <ContentState
          className="crm-lead-list__notice"
          kind="error"
          headingLevel={2}
          title={
            notice === "REFRESH_FAILED"
              ? "The lead list could not be refreshed."
              : "More leads could not be loaded."
          }
          actions={
            <button
              type="button"
              onClick={notice === "REFRESH_FAILED" ? onRefresh : onLoadMore}
            >
              Try again
            </button>
          }
        >
          <p>The last confirmed lead information remains available below.</p>
        </ContentState>
      )}

      <p className="crm-lead-list__filter-scope" id="crm-lead-filter-scope">
        Filters and sorting apply only to leads currently loaded in this view.
      </p>

      <div
        className="crm-lead-list__toolbar"
        role="group"
        aria-label="Loaded lead controls"
        aria-describedby="crm-lead-filter-scope"
      >
        <FormField id="crm-lead-stage-filter" label="Loaded pipeline stage">
          {(controlProps) => (
            <Select
              {...controlProps}
              value={filters.pipelineStage}
              onChange={(event) =>
                onFiltersChange({
                  ...filters,
                  pipelineStage: event.currentTarget
                    .value as AdminLeadListFilters["pipelineStage"],
                })
              }
            >
              <option value={ALL_LEAD_PIPELINE_STAGES}>All stages</option>
              {adminLeadListStageOptions.map((stage) => (
                <option key={stage} value={stage}>
                  {leadPipelineStageLabels[stage]}
                </option>
              ))}
            </Select>
          )}
        </FormField>

        <FormField id="crm-lead-source-filter" label="Loaded lead source">
          {(controlProps) => (
            <Select
              {...controlProps}
              value={filters.sourceKind}
              onChange={(event) =>
                onFiltersChange({
                  ...filters,
                  sourceKind: event.currentTarget
                    .value as AdminLeadListFilters["sourceKind"],
                })
              }
            >
              <option value={ALL_LEAD_SOURCE_KINDS}>All sources</option>
              <option value={UNRECORDED_LEAD_SOURCE}>Not captured</option>
              {adminLeadListSourceOptions.map((sourceKind) => (
                <option key={sourceKind} value={sourceKind}>
                  {leadSourceKindLabels[sourceKind]}
                </option>
              ))}
            </Select>
          )}
        </FormField>

        <FormField id="crm-lead-sort" label="Sort loaded leads">
          {(controlProps) => (
            <Select
              {...controlProps}
              value={filters.sort}
              onChange={(event) =>
                onFiltersChange({
                  ...filters,
                  sort: event.currentTarget
                    .value as AdminLeadListFilters["sort"],
                })
              }
            >
              <option value="UPDATED_DESC">Recently updated</option>
              <option value="CREATED_DESC">Newest leads</option>
              <option value="NAME_ASC">Lead name</option>
            </Select>
          )}
        </FormField>

        <FormField id="crm-lead-assignment-filter" label="Loaded assignment">
          {(controlProps) => (
            <Select
              {...controlProps}
              value={filters.assignment}
              onChange={(event) =>
                onFiltersChange({
                  ...filters,
                  assignment: event.currentTarget
                    .value as AdminLeadListFilters["assignment"],
                })
              }
            >
              <option value={ALL_LEAD_ASSIGNMENTS}>All assignments</option>
              <option value={ASSIGNED_LEADS}>Assigned</option>
              <option value={UNASSIGNED_LEADS}>Unassigned</option>
            </Select>
          )}
        </FormField>

        <div className="crm-lead-list__toolbar-actions">
          {filtersActive && (
            <button
              type="button"
              className="crm-lead-list__button crm-lead-list__button--secondary"
              onClick={() =>
                onFiltersChange({ ...defaultAdminLeadListFilters })
              }
            >
              Clear filters
            </button>
          )}
          <button
            type="button"
            className="crm-lead-list__button"
            aria-disabled={busy ? "true" : undefined}
            onClick={() => {
              if (!busy) onRefresh();
            }}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <p
        className="crm-lead-list__summary"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {loadingMore
          ? "Loading more leads."
          : refreshing
            ? "Refreshing the lead list."
            : filterLabel(visibleItems.length, items.length)}
      </p>

      {visibleItems.length === 0 ? (
        <ContentState
          kind="empty"
          title="No loaded leads match these filters."
          actions={
            <button
              type="button"
              onClick={() =>
                onFiltersChange({ ...defaultAdminLeadListFilters })
              }
            >
              Clear filters
            </button>
          }
        >
          <p>
            Change or clear the fixed pipeline and source filters to review the
            loaded leads.
          </p>
        </ContentState>
      ) : (
        <DataTable
          regionLabel="Admin lead list"
          caption={filterLabel(visibleItems.length, items.length)}
          columns={columns}
          rows={visibleItems}
          getRowKey={(item) => item.leadId}
        />
      )}

      {nextCursor && (
        <div className="crm-lead-list__pagination">
          <button
            type="button"
            className="crm-lead-list__button crm-lead-list__button--secondary"
            aria-disabled={busy ? "true" : undefined}
            onClick={() => {
              if (!busy) onLoadMore();
            }}
          >
            {loadingMore ? "Loading…" : "Load more leads"}
          </button>
        </div>
      )}
    </div>
  );
}
